// O QUE CADA TIPO DE SERVIÇO CONSOME DA MARGEM, NA REGRA DO ÓRGÃO (#271, ADR-0015).
//
// O catálogo global de tipos de serviço diz o PADRÃO: crédito no balde dele,
// facultativa (saúde, odonto, seguro, mensalidade) fora da margem. Quem decide o
// que vale num órgão é a regra de margem dele, porque a lei muda de órgão para
// órgão — Morada Nova deixa a saúde fora dos 60%, a União a põe nos 35%, a ALECE
// soma tudo num saldo só.
//
// A tabela é a mesma na leitura (a ficha) e na edição (os dois modais). Quem
// edita envia só o que DIFERE do padrão: o resto continua sendo o catálogo, e
// muda junto se o catálogo mudar.
import { DataTable, type Column } from "@atlas/ui/web";
import { TIPOS_SERVICO } from "@atlas/domain";
import type { AdminDestinoMargem, ServicoDaRegraDto, ServicoDaRegraInput } from "@atlas/sdk";
import { DESTINO_LABEL } from "./motor-rubricas-labels";

const DESTINOS: readonly AdminDestinoMargem[] = ["EMPRESTIMO", "CARTAO_CONSIGNADO", "CARTAO_BENEFICIOS", "TOTAL", "NENHUM"];

/** O destino de cada tipo, por código. */
export type MapaDestinos = Record<string, AdminDestinoMargem>;

const PADRAO: MapaDestinos = Object.fromEntries(TIPOS_SERVICO.map((t) => [t.codigo, t.destinoPadrao as AdminDestinoMargem]));

/** O mapa de partida: o que a regra diz, ou o catálogo onde ela não diz. */
export function mapaInicial(servicos?: readonly ServicoDaRegraDto[]): MapaDestinos {
  const mapa: MapaDestinos = { ...PADRAO };
  for (const s of servicos ?? []) mapa[s.tipoServico] = s.destino;
  return mapa;
}

/**
 * O que vai no corpo: só os tipos cujo destino difere do catálogo. A prioridade
 * que a regra já tinha num tipo mapeado é preservada — a tela não a edita, e a
 * correção troca o conjunto inteiro.
 */
export function servicosDoMapa(mapa: MapaDestinos, original?: readonly ServicoDaRegraDto[]): ServicoDaRegraInput[] {
  return TIPOS_SERVICO.filter((t) => mapa[t.codigo] !== PADRAO[t.codigo]).map((t) => {
    const antes = original?.find((s) => s.tipoServico === t.codigo && s.origem === "regra");
    return {
      tipoServico: t.codigo,
      destino: mapa[t.codigo]!,
      ...(antes?.prioridade != null ? { prioridade: antes.prioridade } : {}),
    };
  });
}

/** Algum tipo foi para o teto somado? A regra sem teto não tem o que consumir. */
export function usaTetoSomado(mapa: MapaDestinos): boolean {
  return Object.values(mapa).includes("TOTAL");
}

type Linha = (typeof TIPOS_SERVICO)[number];

/**
 * Uma linha por tipo do catálogo. Sem `onMudar`, só leitura (os `servicos` que a
 * API devolveu); com `onMudar`, cada linha tem um seletor de destino.
 */
export function TabelaServicosDaRegra({
  servicos,
  mapa,
  onMudar,
  caption = "O que cada serviço consome",
}: {
  servicos?: readonly ServicoDaRegraDto[];
  mapa?: MapaDestinos;
  onMudar?: (tipo: string, destino: AdminDestinoMargem) => void;
  caption?: string;
}) {
  const atual = mapa ?? mapaInicial(servicos);
  const origemNaRegra = (tipo: string) =>
    mapa ? atual[tipo] !== PADRAO[tipo] : servicos?.find((s) => s.tipoServico === tipo)?.origem === "regra";

  const colunas: Column<Linha>[] = [
    { key: "servico", header: "Serviço", render: (t) => t.rotulo },
    {
      key: "categoria",
      header: "Categoria",
      render: (t) => (t.categoriaPadrao === "consignacao_credito" ? "Crédito" : "Facultativa"),
    },
    {
      key: "destino",
      header: "Consome",
      render: (t) =>
        onMudar ? (
          <select
            aria-label={`Destino de ${t.rotulo}`}
            value={atual[t.codigo]}
            onChange={(e) => onMudar(t.codigo, e.target.value as AdminDestinoMargem)}
            style={ESTILO_SELECT}
          >
            {DESTINOS.map((d) => (
              <option key={d} value={d}>
                {DESTINO_LABEL[d]}
              </option>
            ))}
          </select>
        ) : (
          DESTINO_LABEL[atual[t.codigo]!]
        ),
    },
    {
      key: "origem",
      header: "Origem",
      render: (t) =>
        origemNaRegra(t.codigo) ? (
          <span style={{ fontWeight: 600 }}>{mapa ? "alterado" : "da regra"}</span>
        ) : (
          <span style={{ color: "var(--text-dim)" }}>padrão</span>
        ),
    },
  ];

  // A tabela não pode ditar a largura de quem a contém. Dentro do Modal (grid com
  // `overflowX: hidden`), o mínimo de conteúdo dela alargava o corpo inteiro, e o
  // que passava da caixa era cortado — percentuais inclusive. Um grid de trilha
  // `minmax(0, 1fr)` tem mínimo zero: a tabela ocupa a largura que houver e, em
  // tela estreita, rola dentro do próprio quadro.
  return (
    <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr)" }}>
      <DataTable columns={colunas} rows={[...TIPOS_SERVICO]} rowKey={(t) => t.codigo} caption={caption} />
    </div>
  );
}

/** O seletor compacto da linha, com os tokens do tema (o nativo vem claro no escuro). */
const ESTILO_SELECT: React.CSSProperties = {
  background: "var(--bg-elev)",
  color: "var(--text)",
  border: "1px solid var(--border-strong)",
  borderRadius: 8,
  padding: "4px 8px",
  fontSize: 13,
};
